import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { storage } from "../utils/storage";
import UIButton from "../components/Button";
import UIPopup from "../components/UIPopup";
const baseUrl = process.env.REACT_APP_API_CONFIG;

const reportReasons = [
  "Rude or abusive behavior",
  "Inappropriate content",
  "Underage",
  "I'm just not interested",
  "Other",
];

export default function Report() {
  const { messageId } = useParams<{ messageId: string }>();
  const navigate = useNavigate();
  const [selectedReason, setSelectedReason] = useState<string>(
    reportReasons[0]
  );
  const [description, setDescription] = useState<string>("");
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    selectedReason !== "Other" ||
    (selectedReason === "Other" && description.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError("Please provide a description for your report");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const token = storage.getJwtToken();
      if (!token) {
        throw new Error("User not authenticated");
      }

      await axios.post(
        `${baseUrl}/report-messages`,
        {
          message_id: messageId,
          reason: selectedReason,
          description: selectedReason === "Other" ? description : undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setShowSubmitted(true);
    } catch (error) {
      console.error("Failed to submit report:", error);
      setError("Failed to submit report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setError(null);
  }, [selectedReason, description]);

  return (
    <div className="bg-black h-screen w-full flex flex-col justify-between">
      <div className="p-4">
        <div className="cursor-pointer">
          {reportReasons.map((reason, index) => (
            <div
              key={index}
              className={`flex items-center p-4 ${
                selectedReason === reason ? "bg-grey rounded-xl" : ""
              }`}
              onClick={() => setSelectedReason(reason)}>
              <div>
                {selectedReason === reason ? (
                  <img
                    src="/assets/images/icon-check-mark.svg"
                    alt="Selected"
                    className="w-6 h-6"
                  />
                ) : (
                  <img
                    src="/assets/images/icon-circle.svg"
                    alt="Selected"
                    className="w-6 h-6"
                  />
                )}
              </div>
              <span className="ml-3 text-white">{reason}</span>
            </div>
          ))}

          {selectedReason === "Other" && (
            <div className="mt-4">
              <textarea
                placeholder="Describe your issue"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full h-[200px] p-4 bg-grey text-white rounded-xl resize-none outline-none focus:ring-2 focus:ring-[#00875A]"
              />
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            </div>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 left-0 right-0 p-4 bg-black">
        <UIButton
          text={isSubmitting ? "Submitting..." : "Submit"}
          textColor="text-black"
          onClick={handleSubmit}
          backgroundColor="btn-color"
          classButton="w-full font-bold text-base"
          isValid={!isSubmitting && canSubmit}
        />
      </div>

      {showSubmitted && (
        <UIPopup
          isOpen={showSubmitted}
          onClose={() => {
            setShowSubmitted(false);
            navigate(-1);
          }}
          title="Submitted"
          primaryButton={{
            text: "I got it",
            onClick: () => {
              setShowSubmitted(false);
              navigate(-1);
            },
            backgroundColor: "btn-color",
            textColor: "text-black",
          }}
          children="Your report was submitted successfully. We will process it and inform you as soon as possible"
          secondaryButton={{
            text: "Cancel",
            onClick: () => {
              setShowSubmitted(false);
              navigate(-1);
            },
            backgroundColor: "btn-color",
            textColor: "text-black",
            isShow: false,
          }}
        />
      )}
    </div>
  );
}
